import React, { useState, useEffect } from "react";
import {
  View,
  ScrollView,
  TouchableOpacity,
  Image,
  Text,
  Platform,
  ActivityIndicator,
} from "react-native";
import { useNavigation } from "@react-navigation/native";
import { DEVICE_SIZES, useDeviceSize } from "rn-responsive-styles";
import Header from "../Common/Header";
import { navigateToScreen } from "../../../../utility/NavigationHelper";
import { AppScreens } from "../../../Utility/AppScreens";
import EventNameSection from "./components/EventNameSection";
import SponsorSection from "../../../../Components/live-streaming-common-components/SponsorSection";
import LiveStream from "../../../../Components/live-streaming-common-components/LiveStream";
import MobileGiftBottomSheet from "./components/MobileGiftBottomSheet";
import TokenPurchaseBottomSheet from "./components/TokenPurchaseBottomSheet";
import SoloStreamChat from "./components/Chat/SoloStreamChat";

import { giftData } from "../../../../Api/configs";
import { SoloStreamingPageProps } from "./types";
import { useSoloStream } from "./hooks/useSoloStream";
import { useGiftHandler } from "./hooks/useGiftHandler";
import { useChatHandler } from "./hooks/useChatHandler";
import { AppColors } from "../../../../utility/AppColors";
import { AppImages } from "../../../../utility/AppImages";
import { LinearGradient } from "expo-linear-gradient";
import { useStreamingOAuth } from "../../../../hooks/useStreamingOAuth";
import InviteSocialMediaModal from "./components/InviteSocialMediaModal";
import { processPayment } from "../../../../services/profileAPI";
import { toast } from "react-toastify";

/**
 * Solo Streaming Page
 * Route: /streaming/:username
 * Displays a single streamer's live stream with chat and gift functionality
 */
const SoloStreamingPage: React.FC<SoloStreamingPageProps> = ({ route }) => {
  const { username } = route.params || { username: "bangpen" };
  const navigation = useNavigation();
  const deviceSize = useDeviceSize();

  // Custom hooks for state management
  const { streamData, loading, isLive, socket } = useSoloStream(username);

  // Payment verification state
  const [isVerifyingPayment, setIsVerifyingPayment] = useState(false);
  const [paymentVerified, setPaymentVerified] = useState(false);

  // Fetch current user info for chat/gift identity
  // TODO: Move this to a proper UserContext or helper
  const [currentUser, setCurrentUser] = useState<{
    id: string;
    username: string;
    avatarUrl?: string;
  } | null>(null);

  React.useEffect(() => {
    import("@react-native-async-storage/async-storage").then((module) => {
      const AsyncStorage = module.default;
      AsyncStorage.getItem("@loginData").then((jsonValue) => {
        if (jsonValue) {
          const data = JSON.parse(jsonValue);
          // Map login data to expected format
          // data might have different shape depending on auth provider
          setCurrentUser({
            id: data.sub || data.userId || data.id || "guest",
            username: data.username || "Guest",
            avatarUrl:
              data.avatar ||
              data.picture ||
              "https://squadify-avatar.s3.eu-west-2.amazonaws.com/panda.svg",
          });
        }
      });
    });
  }, []);

  // Handle payment verification when returning from Stripe
  useEffect(() => {
    const verifyPayment = async () => {
      console.log("🔍 [PaymentVerify] Starting verification check...");
      console.log("🔍 [PaymentVerify] Platform.OS:", Platform.OS);
      console.log("🔍 [PaymentVerify] paymentVerified:", paymentVerified);
      console.log("🔍 [PaymentVerify] route.params:", route?.params);
      console.log(
        "🔍 [PaymentVerify] window.location.search:",
        Platform.OS === "web" ? window.location.search : "N/A"
      );
      console.log(
        "🔍 [PaymentVerify] window.location.href:",
        Platform.OS === "web" ? window.location.href : "N/A"
      );

      if (Platform.OS !== "web" || paymentVerified) {
        console.log(
          "🔍 [PaymentVerify] Skipping - not web or already verified"
        );
        return;
      }

      // Try to get params from route.params first (React Navigation), then window.location
      const params = route?.params as any;
      let sessionId = params?.session_id;
      let tokenParam = params?.token;
      let cancelParam = params?.cancel;

      // If not in route.params, try window.location.search
      if (!sessionId && window.location.search) {
        const urlParams = new URLSearchParams(window.location.search);
        sessionId = urlParams.get("session_id");
        tokenParam = urlParams.get("token");
        cancelParam = urlParams.get("cancel");
      }

      // Also try from the full URL in case it's encoded differently
      if (!sessionId && Platform.OS === "web") {
        const fullUrl = window.location.href;
        const urlMatch = fullUrl.match(/session_id=([^&]+)/);
        if (urlMatch) {
          sessionId = urlMatch[1];
          tokenParam = fullUrl.includes("token=true") ? "true" : null;
          cancelParam = fullUrl.includes("cancel=true") ? "true" : null;
        }
      }

      console.log("🔍 [PaymentVerify] sessionId:", sessionId);
      console.log("🔍 [PaymentVerify] tokenParam:", tokenParam);
      console.log("🔍 [PaymentVerify] cancelParam:", cancelParam);

      // Check if this is a cancel redirect
      if (cancelParam === "true" || cancelParam === true) {
        toast.info("Payment was cancelled");
        // Clean up URL
        const newUrl = `${window.location.pathname}`;
        window.history.replaceState({}, "", newUrl);
        return;
      }

      // Only process if token=true and session_id is present (handle both boolean and string)
      const isTokenValid = tokenParam === "true" || tokenParam === true;
      if (!sessionId || !isTokenValid) {
        console.log(
          "🔍 [PaymentVerify] No session_id or token!=true, skipping"
        );
        return;
      }

      setIsVerifyingPayment(true);

      try {
        // Get access token
        const AsyncStorage = (
          await import("@react-native-async-storage/async-storage")
        ).default;
        const loginDataJson = await AsyncStorage.getItem("@loginData");
        if (!loginDataJson) {
          toast.error("Please login to verify payment");
          setIsVerifyingPayment(false);
          return;
        }

        const loginData = JSON.parse(loginDataJson);
        const accessToken = loginData?.AccessToken || loginData?.accessToken;

        if (!accessToken) {
          toast.error("Authentication required");
          setIsVerifyingPayment(false);
          return;
        }

        console.log("🔍 Verifying payment session:", sessionId);

        // Call processPayment API
        const response = await processPayment(sessionId, accessToken);
        console.log("📦 Payment verification response:", response);

        // Parse response if needed
        let responseData = response;
        if (typeof response === "string") {
          try {
            responseData = JSON.parse(response);
          } catch (e) {
            console.error("Failed to parse response:", e);
          }
        }

        if (responseData?.paymentStatus === "completed") {
          toast.success(
            `Payment successful! ${responseData.tokensAdded || ""} tokens added`
          );
          setPaymentVerified(true);
        } else {
          toast.error(responseData?.message || "Payment verification failed");
        }
      } catch (error: any) {
        console.error("Payment verification error:", error);
        toast.error(error?.message || "Failed to verify payment");
      } finally {
        setIsVerifyingPayment(false);
        // Always clean up URL (remove query params) - even on error
        if (Platform.OS === "web") {
          const newUrl = `${window.location.pathname}`;
          window.history.replaceState({}, "", newUrl);
        }
      }
    };

    verifyPayment();
  }, [paymentVerified]);

  const { userTokens, sendGift, showTokenPurchase, setShowTokenPurchase } =
    useGiftHandler(socket, streamData?.id, streamData?.username);

  const { messages, sendMessage, sendLike, totalViewers, likes } =
    useChatHandler(
      socket,
      streamData?.id,
      currentUser?.id || "",
      currentUser?.username || "",
      currentUser?.avatarUrl
    );

  // UI state
  const [chatOpen, setChatOpen] = useState(true);
  const [showMobileGiftSheet, setShowMobileGiftSheet] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedGiftId, setSelectedGiftId] = useState<string | null>(null);
  const [selectedTokenPackage, setSelectedTokenPackage] = useState<{
    tokens: number;
    price: string;
    exchangeRate: string;
  } | null>(null);

  // Token packages for purchase
  const TOKEN_PACKAGES = [
    { tokens: 577, price: "£5.99", exchangeRate: "£0.01" },
    { tokens: 1127, price: "£12.79", exchangeRate: "£0.01" },
    { tokens: 2227, price: "£26.29", exchangeRate: "£0.01" },
    { tokens: 4427, price: "£52.99", exchangeRate: "£0.01" },
    { tokens: 8827, price: "£106.79", exchangeRate: "£0.01" },
    { tokens: 20327, price: "£246.99", exchangeRate: "£0.01" },
  ];

  const isMobile =
    deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM;
  const isDesktop = deviceSize === DEVICE_SIZES.XL;

  if (loading) {
    return (
      <View style={{ flex: 1, backgroundColor: "#000" }}>
        <Header
          btnSearchTapped={() =>
            navigateToScreen(navigation, AppScreens.SearchEventWebScreen)
          }
          btnWalletTapped={() => {}}
          btnStartTutorialTapped={() => {}}
          btnMessageTapped={() => {}}
          btnNotificationTapped={() => {}}
          btnProfileTapped={() => {}}
        />
        <View
          style={{ flex: 1, justifyContent: "center", alignItems: "center" }}
        >
          <Text style={{ color: "#fff" }}>Loading stream...</Text>
        </View>
      </View>
    );
  }

  if (!streamData) {
    return (
      <View style={{ flex: 1, backgroundColor: "#000" }}>
        <Header
          btnSearchTapped={() =>
            navigateToScreen(navigation, AppScreens.SearchEventWebScreen)
          }
          btnWalletTapped={() => {}}
          btnStartTutorialTapped={() => {}}
          btnMessageTapped={() => {}}
          btnNotificationTapped={() => {}}
          btnProfileTapped={() => {}}
        />
        <View
          style={{ flex: 1, justifyContent: "center", alignItems: "center" }}
        >
          <Text style={{ color: "#fff" }}>Streamer not found</Text>
        </View>
      </View>
    );
  }

  const handleGiftSelect = (gift: any) => {
    sendGift(gift);
    setSelectedGiftId(null);
    setShowMobileGiftSheet(false);
  };

  const streamUrl = `${window.location.origin}/streaming/${username}`;

  return (
    <View style={{ flex: 1, backgroundColor: "#000000" }}>
      <Header
        btnSearchTapped={() =>
          navigateToScreen(navigation, AppScreens.SearchEventWebScreen)
        }
        btnWalletTapped={() => {}}
        btnStartTutorialTapped={() => {}}
        btnMessageTapped={() => {}}
        btnNotificationTapped={() => {}}
        btnProfileTapped={() => {}}
      />

      <View style={{ flex: 1 }}>
        <View style={{ paddingHorizontal: 20, paddingVertical: 10 }}>
          <View style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
            <LinearGradient
              colors={["#1F2937", "#111827"]}
              start={[0, 0]}
              end={[1, 1]}
              style={{
                width: 40,
                height: 40,
                padding: 6,
                borderRadius: 8,
                borderWidth: 0.8,
                borderColor: "#374151",
                justifyContent: "center",
                alignItems: "center",
                aspectRatio: 1,
              }}
            >
              <TouchableOpacity
                onPress={() =>
                  navigateToScreen(navigation, AppScreens.HomeScreen)
                }
                style={{
                  width: "100%",
                  height: "100%",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Image
                  source={AppImages.icArrowLeft}
                  style={{ width: 24, height: 24 }}
                  resizeMode="contain"
                />
              </TouchableOpacity>
            </LinearGradient>
            <Text style={{ color: "white", fontSize: 16, fontWeight: "700" }}>
              Back
            </Text>
          </View>
        </View>
        {isMobile ? (
          /* Mobile Layout */
          <View style={{ flex: 1 }}>
            <EventNameSection
              onTimerEnd={() => {}}
              eventName={streamData.title}
            />
            <SponsorSection />

            {/* Stream Player */}
            <View style={{ backgroundColor: "#000", padding: 8 }}>
              <View
                style={{
                  aspectRatio: 16 / 9,
                  backgroundColor: "#111827",
                  borderRadius: 8,
                  overflow: "hidden",
                }}
              >
                {streamData && streamData.isLive && streamData.streamUrl ? (
                  <View style={{ width: "100%", height: "100%" }}>
                    <LiveStream
                      platform={streamData.platform}
                      streamId={streamData.streamUrl}
                      width="100%"
                      height="100%"
                    />
                    {streamData.streamStatus === "paused" && (
                      <View
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          backgroundColor: "rgba(0,0,0,0.7)",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <Text
                          style={{
                            color: "white",
                            fontSize: 16,
                            fontWeight: "bold",
                          }}
                        >
                          Stream Paused
                        </Text>
                      </View>
                    )}
                  </View>
                ) : (
                  <View
                    style={{
                      flex: 1,
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <Text style={{ color: "#999" }}>Stream Offline</Text>
                  </View>
                )}
              </View>

              {/* Stream Info */}
              <View style={{ flexDirection: "row", marginTop: 12, gap: 12 }}>
                <Image
                  source={{ uri: streamData.avatarUrl }}
                  style={{ width: 48, height: 48, borderRadius: 24 }}
                />
                <View style={{ flex: 1 }}>
                  <Text
                    style={{ color: "#fff", fontSize: 18, fontWeight: "700" }}
                  >
                    {streamData.title}
                  </Text>
                  <View
                    style={{
                      flexDirection: "row",
                      alignItems: "center",
                      gap: 8,
                      marginTop: 4,
                    }}
                  >
                    <View
                      style={{
                        backgroundColor: streamData.isLive ? "#DC2626" : "#666",
                        paddingHorizontal: 8,
                        paddingVertical: 2,
                        borderRadius: 4,
                      }}
                    >
                      <Text
                        style={{
                          color: "#fff",
                          fontSize: 12,
                          fontWeight: "600",
                        }}
                      >
                        {streamData.isLive ? "LIVE" : "OFFLINE"}
                      </Text>
                    </View>
                    <Text style={{ color: "#999", fontSize: 14 }}>
                      @{streamData.username}
                    </Text>
                  </View>
                </View>
              </View>
            </View>

            {/* Mobile Chat Section - Using StreamChat component */}
            <View style={{ flex: 1 }}>
              <SoloStreamChat
                messages={messages}
                onSendMessage={sendMessage}
                likes={likes}
                onLike={sendLike}
                totalViewers={totalViewers}
                isMobile={true}
                onOpenShareModal={() => setShowShareModal(true)}
                onGift={() => setShowMobileGiftSheet(true)}
                onShare={() => setShowShareModal(true)}
                streamId={streamData?.id || ""}
              />
            </View>
          </View>
        ) : (
          /* Desktop Layout */
          <View style={{ flex: 1, flexDirection: "row" }}>
            {/* Left Side - Stream (70%) */}
            <View style={{ flex: 0.7, backgroundColor: "#000" }}>
              <ScrollView showsVerticalScrollIndicator={false}>
                <EventNameSection
                  onTimerEnd={() => {}}
                  eventName={streamData.title}
                />
                <SponsorSection />

                {/* Stream Player Container */}
                <View style={{ padding: 12 }}>
                  <LinearGradient
                    colors={[
                      "rgba(31, 41, 55, 0.50)",
                      "rgba(17, 24, 39, 0.50)",
                    ]}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 1 }}
                    style={{
                      borderRadius: 8,
                      borderWidth: 1,
                      borderColor: "#374151",
                      padding: 12,
                    }}
                  >
                    <View
                      style={{
                        aspectRatio: 16 / 9,
                        backgroundColor: "#111827",
                        borderRadius: 8,
                        overflow: "hidden",
                        position: "relative",
                      }}
                    >
                      {streamData &&
                      streamData.isLive &&
                      streamData.streamUrl ? (
                        <View style={{ width: "100%", height: "100%" }}>
                          {/* Using LiveStream component which handles YouTube/Twitch */}
                          {/* NOTE: streamUrl in streamData is now the ID or full URL depending on provider */}
                          <LiveStream
                            platform={streamData.platform}
                            streamId={streamData.streamUrl}
                            width="100%"
                            height="100%"
                          />
                          {streamData.streamStatus === "paused" && (
                            <View
                              style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: "rgba(0,0,0,0.7)",
                                justifyContent: "center",
                                alignItems: "center",
                              }}
                            >
                              <Text
                                style={{
                                  color: "white",
                                  fontSize: 20,
                                  fontWeight: "bold",
                                }}
                              >
                                Stream Paused
                              </Text>
                            </View>
                          )}
                        </View>
                      ) : (
                        <View
                          style={{
                            flex: 1,
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          <Text style={{ color: "#999", fontSize: 18 }}>
                            Stream Offline
                          </Text>
                          <Text
                            style={{
                              color: "#666",
                              fontSize: 14,
                              marginTop: 8,
                            }}
                          >
                            @{username} is not streaming right now
                          </Text>
                        </View>
                      )}
                    </View>
                  </LinearGradient>
                </View>

                {/* Gift Section - Desktop */}
                <View style={{ padding: 12 }}>
                  <LinearGradient
                    colors={[
                      "rgba(31, 41, 55, 0.50)",
                      "rgba(17, 24, 39, 0.50)",
                    ]}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 1 }}
                    style={{
                      position: "relative",
                      borderRadius: 8,
                      borderWidth: 1,
                      borderColor: "#374151",
                      padding: 12,
                    }}
                  >
                    <View
                      style={{
                        flexDirection: "row",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: 12,
                      }}
                    >
                      <Text
                        style={{
                          color: "#fff",
                          fontSize: 18,
                          fontWeight: "700",
                        }}
                      >
                        Select gift and cheer on the player
                      </Text>
                      <View
                        style={{
                          flexDirection: "row",
                          gap: 12,
                          alignItems: "center",
                        }}
                      >
                        <Text style={{ color: "#fff", fontWeight: "600" }}>
                          Token Balance
                        </Text>
                        <View
                          style={{
                            flexDirection: "row",
                            gap: 4,
                            alignItems: "center",
                          }}
                        >
                          <Image
                            source={AppImages.tokensIcon}
                            style={{ height: 19, width: 19 }}
                            resizeMode="contain"
                          />
                          <Text style={{ color: "#fff", fontWeight: "600" }}>
                            {userTokens}
                          </Text>
                          <TouchableOpacity
                            onPress={() =>
                              setShowTokenPurchase(!showTokenPurchase)
                            }
                            style={{
                              marginLeft: 8,
                              backgroundColor: "#111827",
                              paddingHorizontal: 8,
                              paddingVertical: 6,
                              borderRadius: 6,
                              borderColor: "#505050",
                              borderWidth: 1,
                              flexDirection: "row",
                              alignItems: "center",
                              gap: 6,
                            }}
                          >
                            <Image
                              source={AppImages.icRechargeIcon}
                              style={{ height: 15, width: 15 }}
                              resizeMode="contain"
                            />
                            <Text
                              style={{
                                color: "#fff",
                                fontSize: 10,
                                fontWeight: "700",
                              }}
                            >
                              Recharge
                            </Text>
                            <Image
                              source={
                                showTokenPurchase
                                  ? AppImages.arrowDown
                                  : AppImages.arrowRight
                              }
                              style={{
                                height: 10,
                                width: 10,
                                tintColor: "white",
                              }}
                              resizeMode="contain"
                            />
                          </TouchableOpacity>
                        </View>
                      </View>
                    </View>

                    {/* Gifts Grid */}
                    <ScrollView
                      horizontal
                      showsHorizontalScrollIndicator={false}
                    >
                      <View style={{ flexDirection: "row", gap: 12 }}>
                        {giftData.map((gift) => (
                          <TouchableOpacity
                            key={gift.id}
                            onPress={() => setSelectedGiftId(String(gift.id))}
                            style={{
                              backgroundColor:
                                selectedGiftId === String(gift.id)
                                  ? "rgba(220, 38, 38, 0.10)"
                                  : "#1E1E2E",
                              padding: 12,
                              borderRadius: 8,
                              alignItems: "center",
                              minWidth: 100,
                              borderColor:
                                selectedGiftId === String(gift.id)
                                  ? "#DC2626"
                                  : "transparent",
                              borderWidth: 1,
                            }}
                          >
                            <Image
                              source={gift.gift}
                              style={{ width: 40, height: 40 }}
                              resizeMode="contain"
                            />
                            <Text
                              style={{
                                color: "#fff",
                                marginTop: 4,
                                fontSize: 12,
                                fontWeight: "600",
                              }}
                            >
                              {gift.giftName}
                            </Text>
                            <View
                              style={{
                                flexDirection: "row",
                                gap: 4,
                                marginTop: 4,
                              }}
                            >
                              <Image
                                source={AppImages.tokensIcon}
                                style={{ height: 14, width: 14 }}
                                resizeMode="contain"
                              />
                              <Text style={{ color: "#fff", fontSize: 12 }}>
                                {gift.price}
                              </Text>
                            </View>
                          </TouchableOpacity>
                        ))}
                      </View>
                    </ScrollView>

                    {/* Desktop Send Gift Button */}
                    {selectedGiftId && (
                      <View style={{ marginTop: 16, alignItems: "flex-end" }}>
                        <TouchableOpacity
                          onPress={() => {
                            const gift = giftData.find(
                              (g) => String(g.id) === selectedGiftId
                            );
                            if (gift) handleGiftSelect(gift);
                          }}
                          style={{
                            backgroundColor: "#DC2626",
                            paddingHorizontal: 24,
                            paddingVertical: 10,
                            borderRadius: 8,
                            flexDirection: "row",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          <Text
                            style={{
                              color: "#fff",
                              fontWeight: "700",
                              fontSize: 14,
                            }}
                          >
                            Send Gift
                          </Text>
                          <Image
                            source={AppImages.arrowRight}
                            style={{ width: 14, height: 14, tintColor: "#fff" }}
                            resizeMode="contain"
                          />
                        </TouchableOpacity>
                      </View>
                    )}

                    {/* Desktop Token Popup Overlay - Moved to end for Z-Index */}
                    {!isMobile && showTokenPurchase && (
                      <TokenPurchaseBottomSheet
                        variant="popup"
                        visible={showTokenPurchase}
                        onClose={() => {
                          setShowTokenPurchase(false);
                          setSelectedTokenPackage(null);
                        }}
                        tokenPackages={TOKEN_PACKAGES}
                        selectedPackage={selectedTokenPackage}
                        handlePackageSelect={(pkg) =>
                          setSelectedTokenPackage(pkg)
                        }
                        streamId={streamData?.id || ""}
                        streamUsername={username}
                      />
                    )}
                  </LinearGradient>
                </View>
              </ScrollView>
            </View>

            {/* Right Side - Chat (30%) - Using StreamChat component */}
            {chatOpen && (
              <View
                style={{
                  flex: 0.3,
                  height: "100%",
                }}
              >
                <SoloStreamChat
                  messages={messages}
                  onSendMessage={sendMessage}
                  likes={likes}
                  onLike={sendLike}
                  totalViewers={totalViewers}
                  isMobile={false}
                  onOpenShareModal={() => setShowShareModal(true)}
                  onGift={() => setShowMobileGiftSheet(true)}
                  onShare={() => setShowShareModal(true)}
                  streamId={streamData?.id || ""}
                />
              </View>
            )}
          </View>
        )}
      </View>

      {/* Modals */}
      {isMobile && (
        <MobileGiftBottomSheet
          visible={showMobileGiftSheet}
          onClose={() => setShowMobileGiftSheet(false)}
          giftData={giftData}
          handleGiftSelect={handleGiftSelect}
          userTokens={userTokens}
          onOpenTokenPurchase={() => {
            setShowMobileGiftSheet(false);
            setShowTokenPurchase(true);
          }}
          eventStatus={isLive ? "ongoing" : "complete"}
        />
      )}

      {/* Shared Modals (Mobile Only for Token Sheet) */}
      {isMobile && (
        <TokenPurchaseBottomSheet
          visible={showTokenPurchase}
          onClose={() => {
            setShowTokenPurchase(false);
            setSelectedTokenPackage(null);
          }}
          tokenPackages={TOKEN_PACKAGES}
          selectedPackage={selectedTokenPackage}
          handlePackageSelect={(pkg) => setSelectedTokenPackage(pkg)}
          streamId={streamData?.id || ""}
          streamUsername={username}
          variant="modal"
        />
      )}

      <InviteSocialMediaModal
        showModal={showShareModal}
        setShowModal={setShowShareModal}
        streamId={streamData?.id || ""}
        heading="Share Stream"
      />

      {/* Desktop Chat Toggle */}
      {!isMobile && !chatOpen && (
        <TouchableOpacity
          onPress={() => setChatOpen(true)}
          style={{
            position: "absolute",
            right: 20,
            bottom: 20,
            backgroundColor: "#655AFF",
            padding: 16,
            borderRadius: 50,
            shadowColor: "#000",
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.25,
            shadowRadius: 4,
          }}
        >
          <Image
            source={AppImages.icChat}
            style={{ width: 24, height: 24, tintColor: "#fff" }}
            resizeMode="contain"
          />
        </TouchableOpacity>
      )}
    </View>
  );
};

export default SoloStreamingPage;
